import { safeGetItem, safeSetItem } from '../../services/safeStorage';
import {
  EinweisungEntwurf,
  SicherungsStand,
  entwurfOhneUnterschriften,
  entwurfSchluessel,
} from './einweisungLogic';

/**
 * Das Schreiben und Lesen der Entwurfssicherung — die einzige Stelle dieses
 * Moduls, die den Speicher des Geraets anfasst.
 *
 * Die ENTSCHEIDUNGEN (welcher Stand ist neuer, was wird gesichert, wann wird
 * geraeumt) stehen daneben in einweisungLogic.ts und sind ohne DOM pruefbar;
 * hier bleibt nur der Zugriff.
 *
 * ─── Nichts hiervon darf je werfen ─────────────────────────────────────────
 * `localStorage` ist im privaten Modus gesperrt, kann voll sein und ist auf
 * manchen Geraeten durch Richtlinien abgeschaltet. Ein Absturz waere hier
 * besonders bitter: die Sicherung existiert, um Arbeit zu retten, nicht um sie
 * zu gefaehrden. Faellt sie aus, arbeitet die Oberflaeche einfach ohne sie
 * weiter — sichtbar gemacht ueber den zurueckgegebenen Stand.
 *
 * Der Zugriff laeuft ueber services/safeStorage: die Helfer werfen nie und
 * melden mit `false` zurueck, dass der Wert NICHT in den Speicher des Geraets
 * gelangt ist (er liegt dann nur im Arbeitsspeicher und ueberlebt kein
 * Neuladen).
 */

/**
 * Sichert den Entwurf. Passt er nicht, wandert er OHNE Unterschriften ein
 * zweites Mal in den Speicher.
 *
 * WARUM dieser zweite Anlauf: Unterschriften sind PNG-DataURLs und machen den
 * Loewenanteil aus. Ein Blatt mit dreissig Unterschriften sprengt den Platz,
 * den ein Browser einer Herkunft zugesteht — die Texte allein liegen dagegen
 * bei wenigen Kilobyte. Die Texte zu retten und die Bilder zu verlieren ist
 * ungleich besser, als gar nichts zu sichern. Der Anwender erfaehrt es ueber
 * den Rueckgabewert.
 */
export const sichereEntwurf = (entwurf: EinweisungEntwurf): SicherungsStand => {
  const schluessel = entwurfSchluessel(entwurf.einweisungId);

  const schreibe = (wert: EinweisungEntwurf): boolean => {
    try {
      return safeSetItem(schluessel, JSON.stringify(wert));
    } catch {
      // JSON.stringify scheitert an einem Zirkelbezug — kann nicht vorkommen,
      // waere aber ein Absturz mitten im Tippen.
      return false;
    }
  };

  if (schreibe(entwurf)) return 'gesichert';
  if (schreibe(entwurfOhneUnterschriften(entwurf))) return 'ohne-unterschriften';
  return 'aus';
};

/** Der rohe gesicherte Text — die Auswertung macht `pruefeEntwurf`. */
export const leseEntwurf = (einweisungId: string): string | null =>
  safeGetItem(entwurfSchluessel(einweisungId));

/**
 * Raeumt den lokalen Stand.
 *
 * Erst ein leerer Wert, dann das eigentliche Entfernen: safeStorage haelt bei
 * gesperrtem Speicher eine Kopie im Arbeitsspeicher, und die wuerde ein blosses
 * `removeItem` nicht erreichen — der geraeumte Entwurf kaeme beim naechsten
 * Oeffnen zurueck.
 */
export const raeumeEntwurf = (einweisungId: string): void => {
  const schluessel = entwurfSchluessel(einweisungId);
  safeSetItem(schluessel, '');
  try {
    localStorage.removeItem(schluessel);
  } catch {
    // Gesperrter Speicher: der leere Wert oben genuegt.
  }
};
